import { VisitVariantType } from '@csp/csp-common-model';
import { WebHcpPatientVisitEntry } from '@csp/csp-web-content-definitions';
import { ScheduleRequestRef } from '@csp/csp-common-scheduling';
import { isDefined } from '@csp/csp-common-util';
import { DEFAULT_UNSCHEDULED_REQUEST_REF, ScheduleVisitsContent, VisitRequest } from '@csp/csp-common-visits';
import { SelectProps } from '@csp/csp-web-ui';
import { uniqBy, uniqWith } from 'lodash';
import { KitType } from '@csp/dmdp-api-hbs-dto';
import { ScheduleRequestRefKey } from '../../patient-list/patient-list-visit/mapper/ScheduleRequestRefKey';
import { VisitRequestInfo } from '../model/VisitRequestInfo';
import { VisitRequestInfos } from '../model/VisitRequestInfos';
import { SelectItemsVisitOption } from '../model/SelectItemsVisitOption';

const toVisitRequestInfos = (visitRequestInfos: VisitRequestInfo[]): VisitRequestInfos => {
  const requestInfos = uniqBy(visitRequestInfos, requestInfo => ScheduleRequestRefKey.toKey(requestInfo));

  const toSelectItems = (hbsOnly?: SelectItemsVisitOption): SelectProps<ScheduleRequestRefKey>[] =>
    uniqWith(requestInfos, VisitRequest.unscheduledVariantComparator)
      .filter(requestInfo => (hbsOnly ? requestInfo.hbs.enabled : true))
      .map(requestInfo => requestInfo.toSelectItem());

  const toRequestRefs = (scheduleRequestRefKeys: ScheduleRequestRefKey[]): ScheduleRequestRef[] => {
    // We must make sure that if an unscheduled visit variant key is included in the argument,
    // then also the default unscheduled visit request ref should be included in the response.
    const keysAsRefs = ScheduleRequestRefKey.toScheduleRequestRef(scheduleRequestRefKeys);
    const keysAsInfos = keysAsRefs
      .map(ref => requestInfos.find(requestInfo => ScheduleRequestRef.isSameRequestRef(ref, requestInfo)))
      .filter(isDefined);

    const includesUnscheduled = keysAsInfos.some(info => info.variant === VisitVariantType.UNSCHEDULED);
    const scheduleRequestRefs = includesUnscheduled ? [...keysAsRefs, DEFAULT_UNSCHEDULED_REQUEST_REF] : keysAsRefs;

    // We must do a final uniqueness filter because the
    // default unscheduled request ref might be included twice
    return uniqBy(scheduleRequestRefs, requestRef => ScheduleRequestRefKey.toKey(requestRef));
  };

  const getByHbsKitType = (kitType: KitType): VisitRequestInfo[] =>
    requestInfos.filter(requestInfo => requestInfo.hbs.kitTypes.includes(kitType));

  return {
    visitRequestInfos: requestInfos,
    toSelectItems,
    toRequestRefs,
    getByHbsKitType,
  };
};

const from = (
  requests: VisitRequest[],
  content: ScheduleVisitsContent,
  patientVisitTexts: WebHcpPatientVisitEntry,
): VisitRequestInfos => {
  const requestInfos = requests.map(req => VisitRequestInfo.from(req, content, patientVisitTexts));
  return toVisitRequestInfos(requestInfos);
};

const empty = (): VisitRequestInfos => toVisitRequestInfos([]);

export const VisitRequestInfosMapper = {
  from,
  empty,
};
